import { useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { QuipliLink } from '../../../components/link/quipli-link';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { SelectForm } from '../../../components/select-form/select-form';
import { SearchDropdownOption } from '../../../components/dropdown/search-dropdown';
import { Location, LocationSearchPaginationRequest } from '../../../clients/quipli-service/location/location';
import { Badge } from '../../../components/badge/badge';
import { ApiError } from '../../../clients/axios-instance';
import { Tooltip } from '../../../components/tooltip/tooltip';
import { NavLink, useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { toast } from '../../../util/toasts';

export const LocationList = () => {
  const { searchLocations, updateLocationStatus } = useAPI();

  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const [tablePagination, setTablePagination] = useState<PaginationData>();

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<LocationSearchPaginationRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      isActive: params.has('isActive') ? params.get('isActive')! === 'true' : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    (_request) => {
      setIsTableDataLoading(true);
      searchLocations(_request)
        .then(({ items, pagination }) => {
          setTablePagination(pagination);
          setTableData(items.map(buildDataRow));
          setIsTableDataLoading(false);
        })
        .catch(() => {
          setIsTableDataLoading(false);
          setTableData([]);
        });
    },
  );

  const StatusFilterValues: SearchDropdownOption[] = [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ];

  const columns: DataTableColumn[] = [
    { key: 'id', label: '#', showRule: 'OnlyDesktop' },
    { key: 'name', label: 'Name', showRule: 'OnlyDesktop' },
    { key: 'items', label: 'Items', showRule: 'Both', mobileShowRule: 'halfrow' },
    { key: 'default', label: 'Default', showRule: 'Both', mobileShowRule: 'halfrow' },
    { key: 'status', label: 'Status', showRule: 'Both', mobileShowRule: 'halfrow' },
    { key: 'active', label: 'Active', showRule: 'Both', mobileShowRule: 'halfrow' },
    { key: 'actions', label: '', fixedAsLast: true, showRule: 'OnlyDesktop' },
    { key: 'actionsMobile', label: '', fixedAsLast: true, showRule: 'OnlyMobile' },
  ];

  const buildDataRow = (location: Location, index: number) => {
    return {
      mobileHeader: (
        <>
          <Typography
            type="text-md"
            weight="medium"
            className={location.isActive ? 'text-quipli-black-800' : 'text-grey-400'}
          >
            <NavLink
              to={`/manager/shops/form/${location.id}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {location.name}
            </NavLink>
          </Typography>
        </>
      ),
      fields: {
        id: (
          <Typography type="text-sm" className={location.isActive ? 'text-quipli-black-800' : 'text-grey-400'}>
            {index + 1}
          </Typography>
        ),
        name: (
          <Typography type="text-sm" className={location.isActive ? 'text-quipli-black-800' : 'text-grey-400'}>
            {location.name}
          </Typography>
        ),

        items: (
          <>
            <Typography type="text-sm" className={location.isActive ? 'text-quipli-black-800' : 'text-grey-400'}>
              {location.totalProducts > 0 ? (
                <QuipliLink
                  to={`products?locationId=${location.id}`}
                  className={`underline underline-offset-1 text-${location.isActive ? 'quipli-black-800' : 'grey-400'}`}
                >
                  {location.totalProducts}
                </QuipliLink>
              ) : (
                0
              )}
            </Typography>
          </>
        ),
        default: (
          <Typography type="text-sm" className={location.isActive ? 'text-quipli-black-800' : 'text-grey-400'}>
            {location.isDefault ? 'Yes' : 'No'}
          </Typography>
        ),
        status: (
          <Badge text={location.isOffline ? 'Offline' : 'Online'} type={location.isOffline ? 'danger' : 'success'} />
        ),
        active: (
          <Typography type="text-sm" className={location.isActive ? 'text-quipli-black-800' : 'text-grey-400'}>
            {location.isActive ? 'Yes' : 'No'}
          </Typography>
        ),
        actions: <Actions location={location} enableDisableLocation={enableDisableLocation} />,
        actionsMobile: <ActionsMobile location={location} enableDisableLocation={enableDisableLocation} />,
      },
      redirectTo: `shops/form/${location.id}`,
    };
  };

  const enableDisableLocation = async (location: Location, enable: boolean) => {
    updateLocationStatus(location.id, enable)
      .then(async () => {
        await fetchCallback();
        toast('Successfully updated!', 'success');
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error updating location';
        toast(errMsg, 'error');
      });
  };

  return (
    <>
      <div className="location-list">
        <div className="container-v2 relative p-2">
          <div className="flex justify-between items-center">
            <Typography type="display-sm" weight="medium">
              Manage Locations
            </Typography>
          </div>

          <div className="pt-5 pb-5 flex flex-wrap justify-between gap-2 items-start">
            <div className="order-2 md:order-1 flex gap-2">
              <div className="flex gap-2 flex-wrap">
                <SearchBar
                  data-cy="search-locations"
                  placeholder="Search by keyword"
                  defaultValue={request.keyword}
                  setRequest={setRequest}
                />

                <SelectForm
                  className="h-full w-fit"
                  options={StatusFilterValues}
                  value={
                    request.isActive !== undefined
                      ? StatusFilterValues.find((v) => v.value === (request.isActive ? 'yes' : 'no'))
                      : null
                  }
                  onChange={(ps) => {
                    setRequest({ ...request, isActive: ps ? ps.value === 'yes' : undefined });
                  }}
                  isClearable={true}
                  placeholder="Active"
                  isSearchable={false}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      height: 'inherit',
                    }),
                  }}
                />
              </div>
            </div>
            <div className="hidden md:block order-1 md:order2 flex gap-2">
              <Button className="button-yellow !border-0 h-[46px]" data-cy="add-location">
                <QuipliLink to="shops/form">
                  <Typography
                    type="text-md"
                    weight="semi-bold"
                    className="flex justify-center justify-items-center gap-1 text-grey-700"
                  >
                    <Icon icon="Plus" stroke="var(--grey-700)" />
                    Add Location
                  </Typography>
                </QuipliLink>
              </Button>
            </div>
          </div>

          <DataTable
            isLoading={isTableDataLoading}
            columnNames={columns}
            data={tableData}
            pagination={tablePagination}
            request={request}
            setRequest={setRequest}
            cypressPrevious="previous_renters_page"
            cypressNext="next_renters_page"
            data-cy="location-table"
          />
        </div>
      </div>
      <div className="block md:hidden fixed left-0 right-0 bottom-2 w-full p-2 mr-[-10px]">
        <Button className="button-yellow w-full" data-cy="add-location">
          <QuipliLink to="shops/form">
            <Typography
              type="text-md"
              weight="semi-bold"
              className="flex justify-center justify-items-center gap-1 text-grey-700"
            >
              <Icon icon="Plus" stroke="var(--grey-700)" />
              Add Location
            </Typography>
          </QuipliLink>
        </Button>
      </div>
    </>
  );
};

const Actions = ({
  location,
  enableDisableLocation,
}: {
  location: Location;
  enableDisableLocation: (location: Location, enable: boolean) => void;
}) => {
  return (
    <div>
      {location.isActive ? (
        <Tooltip tooltip={location.isDefault ? 'Default location cannot be disabled' : 'Click to disable'} type="black">
          <Button
            className="button-tertiary"
            onClick={() => {
              enableDisableLocation(location, false);
            }}
            disabled={location.isDefault}
            data-cy="disable-location"
          >
            <Icon icon="CheckCircle" stroke="var(--success-500)" />
          </Button>
        </Tooltip>
      ) : (
        <Tooltip tooltip={'Click to enable'} type="black">
          <Button
            className="button-tertiary"
            onClick={() => {
              enableDisableLocation(location, true);
            }}
            data-cy="enable-location"
          >
            <Icon icon="CheckCircle" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

const ActionsMobile = ({
  location,
  enableDisableLocation,
}: {
  location: Location;
  enableDisableLocation: (location: Location, enable: boolean) => void;
}) => {
  return (
    <div className="w-full pl-2 pr-2 mt-2">
      <Button className="button-secondary w-full mb-2" data-cy="edit-location">
        <QuipliLink to={`shops/form/${location.id}`}>
          <Typography
            type="text-xs"
            weight="semi-bold"
            className="flex justify-center justify-items-center gap-1 text-grey-700"
          >
            Edit Location
          </Typography>
        </QuipliLink>
      </Button>
      {location.isActive ? (
        <Button
          className="button-secondary w-full"
          onClick={() => {
            enableDisableLocation(location, false);
          }}
          disabled={location.isDefault}
          data-cy="disable-location"
        >
          <Typography
            type="text-xs"
            weight="semi-bold"
            className="flex justify-center justify-items-center gap-1 text-grey-700"
          >
            Disable location
          </Typography>
        </Button>
      ) : (
        <Button
          className="button-secondary w-full"
          onClick={() => {
            enableDisableLocation(location, true);
          }}
          data-cy="enable-location"
        >
          <Typography
            type="text-xs"
            weight="semi-bold"
            className="flex justify-center justify-items-center gap-1 text-grey-700"
          >
            Enable location
          </Typography>
        </Button>
      )}
    </div>
  );
};
